import { useEffect, useMemo, useRef, useState } from 'react';
import {
  tAddWorkspaceUserForm,
  tDeleteUserForm,
  tEditWorkspaceUserForm,
  tPipeOption,
  tTeamContext,
  tUsersPipesPermissions,
} from './Team.types';
import { FormikConfig, useFormik } from 'formik';
import {
  AddWorkspaceUserSchema,
  DeleteUserSchema,
  EditUserSchema,
} from './Team.schemas';
import * as Yup from 'yup';
import {
  tAddWorkspaceUserPayload,
  tToggleUserInboxPayload,
  tWorkspaceUserInfo,
} from '../../../../providers/WorkspaceProvider/Workspace.types';
import { eUserRole } from '../../../../providers/UserProvider/User.enums';
import { useWorkspaceContext } from '../../../../providers/WorkspaceProvider/Workspace.context';
import { useToastContext } from '../../../../providers/ToastProvider/Toast.hook';
import { eAlertType } from '../../../../providers/ToastProvider/Toast.enum';
import { useUserContext } from '../../../../providers/UserProvider/User.context';
import { isEmpty, isEqual, isUndefined, uniqBy } from 'lodash';
import { teamService } from './Team.service';
import { useSettingsContext } from '../../Settings.context';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useSeatPurchase } from 'src/providers/SubscriptionProvider/useSeatPurchase.hook';
import { useSeatCardAttach } from 'src/providers/SubscriptionProvider/useSeatCardAttach.hook';
import { useSubscriptionAnalytics } from 'src/hooks/analytics/subscription/useSubscriptionAnalytics.hook';
import { SUBSCRIPTION_EVENTS } from 'src/hooks/analytics/subscription/subscriptionAnalytics.constants';
import { getLiveAuthState } from '../../../../firebase/authToken.service';
import { SESSION_MISMATCH_RELOAD_DELAY_MS } from './Team.constants';
import { markAppReload } from 'src/lib/perf/reloadClassifier';
import { eAddUserFailure, eSeatFlowOutcome } from './Team.enums';
import {
  addUserFailureCopy,
  classifyAddUserFailure,
} from './Team.functions';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import {
  TEAM_EVENTS,
  tTeamMemberAddedChannel,
  tTeamMemberAddedSurface,
} from 'src/hooks/analytics/team/teamAnalytics.constants';
import { useTranslation } from 'react-i18next';

/** This hook only ever serves the settings team section. */
const ADD_MEMBER_SURFACE: tTeamMemberAddedSurface = 'settings';

export default function useTeam(): tTeamContext {
  const [addTeamMemberModalIsOpen, setAddTeamMemberModalIsOpen] =
    useState<boolean>(false);
  // Recorded by whichever surface opened the modal. See `seatPurchaseRequired` in
  // Team.types: the form renders on the Dashboard too, where the settings context
  // that would answer "are we at the limit?" is not mounted.
  const [seatPurchaseRequired, setSeatPurchaseRequired] =
    useState<boolean>(false);
  // U6 — the outcome that decides whether the modal survives the handler's teardown.
  const [seatFlowOutcome, setSeatFlowOutcome] = useState<eSeatFlowOutcome>(
    eSeatFlowOutcome.NONE,
  );
  const [seatFlowMessage, setSeatFlowMessage] = useState<string | null>(null);
  // A seat already bought in THIS modal session. It is what makes the retry after a
  // partial failure invite-only: re-entering the purchase would buy a second seat for
  // the same teammate.
  const seatAlreadyPurchasedRef = useRef<boolean>(false);
  // Survives closing and reopening the modal, unlike the ref above. Normally the
  // post-purchase refresh raises capacity so the reopened flow is no longer at-limit
  // and buys nothing — but if that refresh failed, the flow would look at-limit again
  // and buy a SECOND seat for the same teammate. This remembers the paid seat instead
  // of trusting a number that may not have landed.
  const paidUninvitedWorkspaceRef = useRef<string | null>(null);

  const { getSummary: getSubscriptionSummary } = useSubscriptionContext();
  const { trackSubscriptionEvent } = useSubscriptionAnalytics();
  // Card capture lives here, not in the modal: it has two triggers (the proactive
  // lookup and "use a different card" after a decline) and the purchase below has to
  // know whether a card is pending attachment.
  const [isCardCaptureOpen, setIsCardCaptureOpen] = useState<boolean>(false);
  // Published by whichever at-limit surface is on screen, so the quote is fetched
  // once for the branch decision and reused by the modal instead of each consumer
  // mounting its own copy of the hook.
  const [seatQuoteEnabled, setSeatQuoteEnabled] = useState<boolean>(false);

  // THE seat-purchase instance. Every consumer — the Team tab, the Dashboard, and the
  // add-teammate form — reads this one through context. Separate instances would each
  // run their own products and invoice-preview fetches, and the instance gating the
  // submit button would not be the instance performing the charge.
  const seatPurchase = useSeatPurchase({
    enabled: seatQuoteEnabled || seatPurchaseRequired,
  });
  const { purchaseOneSeat, resetPurchaseIntent } = seatPurchase;
  const { attachCard } = useSeatCardAttach();

  function openAddTeamMemberModal(options: { requiresSeatPurchase: boolean }) {
    // A seat already bought for this workspace and never used is capacity in hand;
    // re-entering the flow must invite against it, not buy another.
    const hasUnusedPaidSeat =
      paidUninvitedWorkspaceRef.current === currentWorkspaceId;
    setSeatPurchaseRequired(
      options.requiresSeatPurchase && !hasUnusedPaidSeat,
    );
    setSeatFlowOutcome(eSeatFlowOutcome.NONE);
    setSeatFlowMessage(null);
    seatAlreadyPurchasedRef.current = hasUnusedPaidSeat;
    setIsCardCaptureOpen(false);
    resetPurchaseIntent();
    setAddTeamMemberModalIsOpen(true);
  }

  /**
   * Closing the flow. Rotates the purchase intent so a later deliberate purchase is a
   * new attempt rather than a replayed one, and reports abandonment when the customer
   * saw a price and left without buying.
   */
  function closeAddTeamMemberModal() {
    if (seatPurchaseRequired && !seatAlreadyPurchasedRef.current) {
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.SEAT_FLOW_ABANDONED, {
        outcome: seatFlowOutcome,
      });
    }
    resetPurchaseIntent();
    setSeatPurchaseRequired(false);
    setSeatFlowOutcome(eSeatFlowOutcome.NONE);
    setSeatFlowMessage(null);
    seatAlreadyPurchasedRef.current = false;
    setIsCardCaptureOpen(false);
    setAddTeamMemberModalIsOpen(false);
  }
  const [showEditUserModal, toggleEditUserModal] = useState<boolean>(false);
  const [showConfirmEditUserModal, setShowConfirmEditUserModal] =
    useState<boolean>(false);
  const [addingUser, setAddingUser] = useState<boolean>(false);
  const [deletingUser, setDeletingUser] = useState<boolean>(false);
  const [updatingUser, setUpdatingUser] = useState<boolean>(false);
  const [showUserCreatedModal, toggleUserCreatedModal] =
    useState<boolean>(false);
  const [toggleUserCreatedModalV2, setToggleUserCreatedModalV2] =
    useState<boolean>(false);
  const [addedUserName, setAddedUserName] = useState<string | null>(null);
  const [showExistentUserModal, toggleExistentUserModal] =
    useState<boolean>(false);
  const [usersPipesPermissions, setUsersPipesPermissions] =
    useState<tUsersPipesPermissions>({});
  const [pipesSelectionOptions, setPipesSelectionOptions] = useState<
    tPipeOption[]
  >([]);
  const [showDeleteModal, toggleDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<tWorkspaceUserInfo | null>(
    null,
  );
  const [userToEdit, setUserToEdit] = useState<tWorkspaceUserInfo | null>(null);
  // Owns the cross-account reload timer so unmounting cancels it (BUG-801):
  // an unowned setTimeout reloads the app minutes later, from whatever screen
  // the user navigated to, with no visible cause.
  const sessionMismatchReloadRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  useEffect(
    () => () => {
      if (sessionMismatchReloadRef.current) {
        clearTimeout(sessionMismatchReloadRef.current);
      }
    },
    [],
  );
  const { trackEvent } = useAnalytics();
  const { addAlert } = useToastContext();
  const {
    workspaceInfo,
    addUserToWorkspace,
    currentWorkspaceId,
    updateWorkspaceUserInbox,
    unlinkWorkspaceUser,
    updateWorkspaceUser,
    resendUserInvitation: resendUserInvitationFromWorkspace,
  } = useWorkspaceContext();
  const { currentUserId, currentUser } = useUserContext();
  const { setCurrentSettingsInfo, refreshSettingsInfoSilently } =
    useSettingsContext();

  const getPipesInfoRef = useRef(getUsersPipesInfo);

  useEffect(() => {
    // Only fetch pipes info if userManagementV2 is not enabled
    // When userManagementV2 is enabled, pipes are managed differently and this API is not needed
    if (!workspaceInfo?.userManagementV2) {
      getPipesInfoRef.current();
    }
  }, [workspaceInfo?.userManagementV2]);

  useEffect(() => {
    // Only fetch pipes info if userManagementV2 is not enabled
    if (currentWorkspaceId && !workspaceInfo?.userManagementV2) {
      getUsersPipesInfo();
    }
  }, [currentWorkspaceId, workspaceInfo?.userManagementV2]);

  useEffect(() => {
    if (userToEdit) {
      editUserFormik?.setFieldValue(
        'pipes',
        usersPipesPermissions[userToEdit.authId] ?? [],
      );
    }
  }, [userToEdit, usersPipesPermissions]);

  useEffect(() => {
    if (!userToEdit) {
      return;
    }
    // Get the latest user data from workspaceInfo to ensure goal is up-to-date
    const latestUser = workspaceInfo?.users[userToEdit.authId] || userToEdit;
    const uniqueValues =
      usersPipesPermissions[userToEdit.authId]?.filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            (obj) => obj.value === item.value && obj.label === item.label,
          ),
      ) || [];
    editUserFormik.setValues({
      firstName: latestUser.name,
      email: latestUser.email,
      role: { value: latestUser.role, label: latestUser.role },
      pipes: uniqueValues,
      goal: latestUser?.goal ?? 0,
    });
  }, [userToEdit, workspaceInfo?.users]);

  useEffect(() => {
    if (userToEdit && !usersPipesPermissions[userToEdit.authId]) {
      const uniquePipes = uniqBy(editUserFormik?.values.pipes || [], 'value');
      setUsersPipesPermissions((prevState) => ({
        ...prevState,
        [userToEdit.authId]: uniquePipes,
      }));
    }
  }, [userToEdit]);

  const inboxIsActiveForAnyUser = useMemo(() => {
    if (isEmpty(workspaceInfo?.users) || isUndefined(workspaceInfo?.users)) {
      return false;
    }
    return (
      Object.values(workspaceInfo.users).some((user) => user.hasInboxActive) ??
      false
    );
  }, [workspaceInfo?.users]);

  const AddUserInitialValues: tAddWorkspaceUserForm = {
    firstName: '',
    email: '',
    role: { value: eUserRole.SELLER, label: eUserRole.SELLER },
  };

  const EditUserInitialValues: tEditWorkspaceUserForm = {
    firstName: '',
    email: '',
    role: { value: eUserRole.SELLER, label: eUserRole.SELLER },
    pipes: [],
    goal: 0,
  };

  const DeleteUserInitialValues: tDeleteUserForm = {
    newRecipientUser: {
      value: currentUser?.authId ?? '',
      label: currentUser?.name ?? '',
    },
  };

  const { t } = useTranslation('settings/team');

  const addUserFormikConfig: FormikConfig<tAddWorkspaceUserForm> = {
    initialValues: AddUserInitialValues,
    validationSchema: AddWorkspaceUserSchema(t),
    onSubmit: addOrCreateWorkspaceUser,
  };

  // Create conditional validation schema: only require pipes if userManagementV2 is NOT enabled
  const editUserValidationSchema = useMemo(() => {
    if (workspaceInfo?.userManagementV2) {
      // When userManagementV2 is enabled, pipes are not used, so make them optional
      return Yup.object({
        role: Yup.object({
          value: Yup.string().required('El rol es requerido'),
          label: Yup.string().required('El rol es requerido'),
        }),
        pipes: Yup.array(), // Optional when userManagementV2 is enabled
        goal: Yup.number()
          .max(999999999999999, 'El objetivo debe ser menor a 999,999,999,999,999')
          .min(0, 'El objetivo debe ser mayor a 0'),
      });
    }
    return EditUserSchema;
  }, [workspaceInfo?.userManagementV2]);

  const editUserFormikConfig: FormikConfig<tEditWorkspaceUserForm> = {
    initialValues: EditUserInitialValues,
    validationSchema: editUserValidationSchema,
    onSubmit: editWorkspaceUser,
  };

  const deleteUserFormikConfig: FormikConfig<tDeleteUserForm> = {
    initialValues: DeleteUserInitialValues,
    validationSchema: DeleteUserSchema,
    onSubmit: deleteWorkspaceUserAndAssignLeads,
  };

  const addUserFormik = useFormik(addUserFormikConfig);

  const editUserFormik = useFormik(editUserFormikConfig);

  const deleteUserFormik = useFormik(deleteUserFormikConfig);

  function userAlreadyExists(email: string) {
    if (isUndefined(workspaceInfo?.users)) {
      return false;
    }
    const matchingEmailUsers = Object.values(workspaceInfo.users).find(
      (user) => user.email === email,
    );
    if (matchingEmailUsers !== undefined) {
      return true;
    }
    return false;
  }

  // The modal is mounted globally, so switching workspace with it open would leave
  // one workspace's priced flow — and its paid-seat state — on screen for another.
  // Subscription.hook resets on the same key for the same reason.
  useEffect(() => {
    setAddTeamMemberModalIsOpen(false);
    setSeatPurchaseRequired(false);
    setSeatFlowOutcome(eSeatFlowOutcome.NONE);
    setSeatFlowMessage(null);
    setIsCardCaptureOpen(false);
    seatAlreadyPurchasedRef.current = false;
    paidUninvitedWorkspaceRef.current = null;
    resetPurchaseIntent();
    // resetPurchaseIntent is stable; re-running on its identity would fire on renders
    // that are not a workspace change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]);

  async function addOrCreateWorkspaceUser(values: tAddWorkspaceUserForm) {
    if (isUndefined(currentUserId) || addingUser) {
      return;
    }
    setAddingUser(true);

    if (userAlreadyExists(values.email)) {
      addAlert({
        type: eAlertType.ERROR,
        message: `El usuario con el correo ${values.email} ya existe`,
        hideTitle: true,
      });
      setAddingUser(false);
      setAddTeamMemberModalIsOpen(false);
      addUserFormik.resetForm();
      return;
    }

    const addWorkspaceUserPayload: tAddWorkspaceUserPayload = {
      name: values.firstName,
      email: values.email,
      role: values.role.value,
      current_user: currentUserId,
      funnels: values.pipes?.map((pipe) => pipe.value) ?? [],
      password: values.password,
    };

    const addWorkspaceUserPayloadV2: tAddWorkspaceUserPayload = {
      name: values.firstName,
      email: values.email,
      role: values.role.value,
    };

    // U6 — the purchase leg. It runs before the invite because the seat is the durable
    // artifact: no transaction spans billing and workspace membership, so the order is
    // chosen to leave the customer with paid capacity rather than an unpaid teammate.
    //
    // Skipped when a seat was already bought in this modal session — that retry is
    // invite-only and must never charge twice for the same teammate (KD7).
    let purchasedThisAttempt = false;
    if (seatPurchaseRequired && !seatAlreadyPurchasedRef.current) {
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.SEAT_PURCHASE_SUBMIT);

      // A card typed into the inline capture block has to be attached BEFORE the
      // charge, or the purchase runs against a customer with no payment method and
      // fails every time. Failing here stops the flow rather than charging into a
      // guaranteed decline.
      if (isCardCaptureOpen) {
        const attached = await attachCard();
        if (!attached.ok) {
          setSeatFlowOutcome(eSeatFlowOutcome.DECLINED);
          setSeatFlowMessage(attached.message);
          setAddingUser(false);
          return;
        }
        setIsCardCaptureOpen(false);
      }

      const purchase = await purchaseOneSeat();

      if (purchase.status === 'failed') {
        trackSubscriptionEvent(SUBSCRIPTION_EVENTS.SEAT_PURCHASE_OUTCOME, {
          outcome: 'failed',
          error_code: purchase.errorCode ?? null,
        });

        // KTD18 — a decline happens BEFORE the seat exists, and without the same
        // teardown suppression the modal closes and the form is wiped on exactly the
        // failure this flow should treat gently. The customer keeps their details and
        // is offered a retry or a different card; card capture is NOT forced open,
        // because a decline can be a transient hold rather than a bad card.
        //
        // The decision comes from the hook, which still had the raw error: by here the
        // failure is a message and one code, and Stripe's decline signal lives in
        // fields that flattening drops.
        const declined = purchase.declined;
        setSeatFlowOutcome(
          declined ? eSeatFlowOutcome.DECLINED : eSeatFlowOutcome.NONE,
        );
        setSeatFlowMessage(purchase.message);

        if (!declined) {
          addAlert({
            type: eAlertType.ERROR,
            message: purchase.message,
            hideTitle: true,
          });
        }

        setAddingUser(false);
        // Deliberately NOT the shared teardown: the modal stays open either way, and
        // a non-decline failure still shows its resolved copy in place.
        return;
      }

      purchasedThisAttempt = true;
      seatAlreadyPurchasedRef.current = true;
      trackSubscriptionEvent(SUBSCRIPTION_EVENTS.SEAT_PURCHASE_OUTCOME, {
        outcome: 'purchased',
      });

      // U7 — refresh capacity the moment the seat exists, BEFORE attempting the
      // invite. Refreshing only after a successful invite leaves capacity stale on a
      // partial failure, and re-entering the flow would buy a second seat for the
      // same teammate.
      // Both halves of server truth: the settings seats and the subscription summary
      // (whose purchasedSeats feed the capacity calculation). A failed refresh is a
      // stale number, never a reported purchase failure.
      await Promise.allSettled([
        refreshSettingsInfoSilently(),
        getSubscriptionSummary(),
      ]);
    }

    let keepModalOpen = false;

    try {
      const userAdditionResponse = await addUserToWorkspace(
        workspaceInfo?.userManagementV2
          ? addWorkspaceUserPayloadV2
          : addWorkspaceUserPayload,
      );

      if (userAdditionResponse?.new_user && !workspaceInfo?.userManagementV2) {
        toggleUserCreatedModal(true);
        getUsersPipesInfo();
      } else if (
        userAdditionResponse?.new_user &&
        workspaceInfo?.userManagementV2
      ) {
        setAddedUserName(values.firstName || values.email || null);
        setToggleUserCreatedModalV2(true);
      } else {
        // User already exists - show UserCreatedConfirmationV2 modal
        setAddedUserName(values.firstName || values.email || null);
        setToggleUserCreatedModalV2(true);
      }
      // KTD9 — this local patch writes `available - 1` as capacity ROSE by one, so on
      // the purchase branch it is wrong the moment it runs and stays wrong if the
      // refetch fails. The purchase path already refreshed from the server above.
      if (!purchasedThisAttempt && !seatAlreadyPurchasedRef.current) {
        setCurrentSettingsInfo((prevState) => ({
          ...prevState,
          seats: {
            available: prevState.seats.available - 1,
            used: prevState.seats.used + 1,
          },
        }));
      }
      // The teammate is in. Whatever seat was paid for has now been used.
      paidUninvitedWorkspaceRef.current = null;
      setSeatFlowOutcome(eSeatFlowOutcome.NONE);
      setSeatFlowMessage(null);
      // The v1 payload creates the account outright; the v2 one leaves the user
      // pending an invitation, so the flag is what tells the two apart.
      const channel: tTeamMemberAddedChannel = workspaceInfo?.userManagementV2
        ? 'invitation'
        : 'direct_create';

      trackEvent(TEAM_EVENTS.MEMBER_ADDED, {
        role: values.role.value,
        surface: ADD_MEMBER_SURFACE,
        channel,
        existing_account: userAdditionResponse?.new_user === false,
      });
    } catch (error) {
      // BUG-801: a 403 here means the backend verified the bearer and rejected
      // the requester — never show the raw AxiosError. The live Firebase uid is
      // read through getLiveAuthState so it is gated on the same bounded init
      // wait the bearer uses; reading firebase.auth.currentUser directly would
      // see null during a slow boot and miss the very case this exists to catch.
      const { uid: liveUid } = await getLiveAuthState();
      const failure = classifyAddUserFailure({
        error,
        liveUid,
        paintedUserId: currentUserId,
      });

      const copy = addUserFailureCopy(failure, error);

      // R5 — the seat was bought and the invitation was not sent. The capacity is
      // genuinely paid for, so this is not a plain error: the modal stays open with
      // the entered details, the price disappears (a priced control beside "invite
      // failed" reads as a second charge), and the retry invites only.
      if (seatAlreadyPurchasedRef.current) {
        keepModalOpen = true;
        paidUninvitedWorkspaceRef.current = currentWorkspaceId ?? null;
        setSeatFlowOutcome(eSeatFlowOutcome.SEAT_PURCHASED_INVITE_FAILED);
        setSeatFlowMessage(t(copy.key, copy.defaultValue));
      } else {
        addAlert({
          type: eAlertType.ERROR,
          message: t(copy.key, copy.defaultValue),
          hideTitle: true,
        });
      }

      // The reload hands recovery to UserProvider's boot-time reconciliation
      // guard, which arbitrates which side is stale and never signs out another
      // tab's legitimate Firebase credential. Delayed so the toast is readable,
      // and tracked in a ref so leaving the page cancels it — an unowned timer
      // reloads the app minutes later, from another screen, with no visible
      // cause.
      if (failure === eAddUserFailure.SESSION_MISMATCH) {
        sessionMismatchReloadRef.current = setTimeout(() => {
          markAppReload();
          window.location.reload();
        }, SESSION_MISMATCH_RELOAD_DELAY_MS);
      }
    } finally {
      setAddingUser(false);
      // The teardown is now conditional. It used to run on EVERY outcome, which closed
      // the modal and wiped the entered email on the one failure where the customer
      // has already paid — leaving R5's retry with nothing to retry against.
      if (!keepModalOpen) {
        setAddTeamMemberModalIsOpen(false);
        setSeatPurchaseRequired(false);
        seatAlreadyPurchasedRef.current = false;
        resetPurchaseIntent();
        addUserFormik.resetForm();
      }
    }
  }

  async function editWorkspaceUser(values: tEditWorkspaceUserForm) {
    if (
      isUndefined(currentWorkspaceId) ||
      isUndefined(currentUserId) ||
      !userToEdit ||
      updatingUser
    ) {
      return;
    }
    setUpdatingUser(true);
    // Get the latest user data from workspaceInfo to ensure we have the most up-to-date goal
    const latestUser = workspaceInfo?.users[userToEdit.authId] || userToEdit;
    // Ensure goal is always a number (handle undefined/null/string cases)
    const goalValue = Number(values.goal) || 0;
    const updatedWorkspaceUser: tWorkspaceUserInfo = {
      ...latestUser,
      role: values.role.value,
      goal: goalValue,
      // Preserve createdAt to maintain sort order
      createdAt: latestUser.createdAt,
    };
    const initialPipes = uniqBy(
      usersPipesPermissions[userToEdit.authId] || [],
      'value',
    );

    try {
      if (values.role.value !== latestUser?.role) {
        await teamService.updateWorkspaceUserRol(
          currentWorkspaceId,
          userToEdit.authId,
          values.role.value,
        );
        trackEvent('front.settings.team.edit.update_member_btn.click', {
          displayName: 'Actualizar miembro del equipo',
          description: 'Con qué frecuencia se actualiza cada campo',
          actionType: 'Role',
        });
      }
      // Use Number() to ensure proper comparison (handles string vs number, undefined vs 0, etc.)
      // Compare both as numbers to avoid type coercion issues
      // Use latestUser to get the most current goal value
      const currentGoal = Number(values.goal) || 0;
      const existingGoal = Number(latestUser?.goal) || 0;
      const goalChanged = currentGoal !== existingGoal;
      
      // Only update goal if it actually changed (don't update all users unnecessarily)
      if (goalChanged) {
        await teamService.updateUserWorkspaceGoal(
          currentWorkspaceId,
          userToEdit.authId,
          goalValue,
        );
        trackEvent('front.settings.team.edit.update_member_btn.click', {
          displayName: 'Actualizar miembro del equipo',
          description: 'Con qué frecuencia se actualiza cada campo',
          actionType: 'Goal',
        });
      }
      if (!isEqual(values.pipes, initialPipes)) {
        const response = await teamService.updateWorkspaceUserFunnels(
          currentWorkspaceId,
          currentUserId,
          userToEdit.uuid,
          values.pipes.map((pipe) => pipe.value),
        );
        if (response.code === 200) {
          localStorage.removeItem('funnelData');
        }
        trackEvent('front.settings.team.edit.update_member_btn.click', {
          displayName: 'Actualizar miembro del equipo',
          description: 'Con qué frecuencia se actualiza cada campo',
          actionType: 'Funnel',
        });
      }

      updateWorkspaceUser(updatedWorkspaceUser);
      setUsersPipesPermissions((prevState) => ({
        ...prevState,
        [userToEdit.authId]: values.pipes,
      }));
      addAlert({
        type: eAlertType.SUCCESS,
        message: `Usuario actualizado correctamente`,
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `Error al actualizar usuario:${error}`,
        hideTitle: true,
      });
    }
    setUpdatingUser(false);
    toggleEditUserModal(false);
  }

  async function getUsersPipesInfo() {
    if (isUndefined(currentWorkspaceId)) {
      return;
    }
    // Skip API call if userManagementV2 is enabled (pipes are managed differently)
    if (workspaceInfo?.userManagementV2) {
      return;
    }
    setUpdatingUser(true);

    try {
      const pipesInfo = await teamService.getPipes(currentWorkspaceId);
      setPipesSelectionOptions([]);
      pipesInfo.permissions.forEach((pipe) => {
        setPipesSelectionOptions((prevState) => [
          ...(prevState ?? []),
          { value: pipe.id, label: pipe.name },
        ]);
        pipe.allowedUsers.forEach((userId) => {
          setUsersPipesPermissions((prevState) => ({
            ...prevState,
            [userId]: [
              ...(prevState[userId] ?? []),
              { value: pipe.id, label: pipe.name },
            ],
          }));
        });
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `Error al obtener la información de los embudos:${error}`,
        hideTitle: true,
      });
    } finally {
      setUpdatingUser(false);
    }
  }

  async function toggleUserInbox(user: tWorkspaceUserInfo) {
    const payload: tToggleUserInboxPayload = {
      can_inbox: !user?.hasInboxActive,
    };
    try {
      await updateWorkspaceUserInbox(user.authId, payload);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `Error al actualizar el inbox del usuario:${error}`,
        hideTitle: true,
      });
    }
  }

  async function resendUserInvitation(user: tWorkspaceUserInfo) {
    if (!currentWorkspaceId) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          'No se pudo identificar el workspace. Por favor, recarga la página.',
        hideTitle: true,
      });
      return;
    }

    try {
      await resendUserInvitationFromWorkspace({
        workspaceId: currentWorkspaceId,
        email: user.email,
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: 'Invitación reenviada exitosamente',
      });
    } catch (error) {
      // Check for CORS or network errors
      const isNetworkError =
        error &&
        typeof error === 'object' &&
        'code' in error &&
        (error.code === 'ERR_NETWORK' ||
          error.code === 'ERR_CORS' ||
          error.code === 'ECONNABORTED');

      const isAxiosError =
        error &&
        typeof error === 'object' &&
        'isAxiosError' in error &&
        error.isAxiosError === true &&
        !('response' in error);

      if (isNetworkError || isAxiosError) {
        addAlert({
          type: eAlertType.ERROR,
          message:
            'Error de conexión. El endpoint puede no estar disponible aún. Por favor, verifica tu conexión o intenta más tarde.',
          hideTitle: true,
        });
        return;
      }

      // Handle other errors
      if (error instanceof Error) {
        addAlert({
          type: eAlertType.ERROR,
          message:
            error.message ||
            'Error al reenviar la invitación. Por favor, inténtalo de nuevo.',
          hideTitle: true,
        });
      } else {
        addAlert({
          type: eAlertType.ERROR,
          message:
            'Error al reenviar la invitación. Por favor, inténtalo de nuevo.',
          hideTitle: true,
        });
      }
    }
  }

  async function deleteWorkspaceUserAndAssignLeads(values: tDeleteUserForm) {
    if (
      isUndefined(currentWorkspaceId) ||
      isUndefined(currentUserId) ||
      !userToDelete ||
      deletingUser
    ) {
      return;
    }
    setDeletingUser(true);
    try {
      await unlinkWorkspaceUser(userToDelete.authId, {
        user_to_assign_leads: values.newRecipientUser.value,
      });
      addAlert({
        type: eAlertType.SUCCESS,
        message: `Usuario eliminado correctamente`,
      });
      setCurrentSettingsInfo((prevState) => ({
        ...prevState,
        seats: {
          available: prevState.seats.available + 1,
          used: prevState.seats.used - 1,
        },
      }));
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message: `Error al eliminar usuario:${error}`,
        hideTitle: true,
      });
    }
    setDeletingUser(false);
    toggleDeleteModal(false);
  }

  return {
    addTeamMemberModalIsOpen,
    seatPurchaseRequired,
    openAddTeamMemberModal,
    closeAddTeamMemberModal,
    seatFlowOutcome,
    seatFlowMessage,
    seatPurchase,
    isCardCaptureOpen,
    openCardCapture: () => setIsCardCaptureOpen(true),
    setSeatQuoteEnabled,
    showEditUserModal,
    toggleEditUserModal,
    addUserFormik,
    editUserFormik,
    deleteUserFormik,
    addingUser,
    showUserCreatedModal,
    toggleUserCreatedModal,
    showExistentUserModal,
    toggleExistentUserModal,
    pipesSelectionOptions,
    inboxIsActiveForAnyUser,
    toggleUserInbox,
    resendUserInvitation,
    showDeleteModal,
    toggleDeleteModal,
    setUserToDelete,
    userToDelete,
    deletingUser,
    setUserToEdit,
    userToEdit,
    updatingUser,
    showConfirmEditUserModal,
    setShowConfirmEditUserModal,
    toggleUserCreatedModalV2,
    setToggleUserCreatedModalV2,
    addedUserName,
    setAddedUserName,
  };
}
